import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { identityApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import type {
  ProviderConfigDetail,
  ProviderConfigIndexEntry,
} from '../types/identity';

/**
 * Org-admin schema-driven provider configuration. Mirrors the conventions in
 * useIdentityCredentials.ts: a single key root, admin-gated queries (skip the
 * request for non-admins so the network panel stays quiet and we avoid the
 * global auth-failure side-effect on 403), and mutations that invalidate the
 * whole subtree so the index badge and the open form both refresh.
 */
const KEY_ROOT = ['org-providers'] as const;

const indexKey = (): QueryKey => [...KEY_ROOT, 'index'];
const detailKey = (providerId: string): QueryKey => [...KEY_ROOT, 'config', providerId];

/** The configurable-providers index — names, configured badges, schemas. */
export function useOrgProviderConfigs() {
  const { isAuthenticated, user } = useAuth();
  const isOrgAdmin = user?.role === 'admin';
  return useQuery<{ providers: ProviderConfigIndexEntry[] }>({
    queryKey: indexKey(),
    queryFn: () => identityApi.org.providerConfigs(),
    enabled: isAuthenticated && isOrgAdmin,
    staleTime: 60 * 1000,
  });
}

/**
 * One provider's config + masked current values. Lazy: only enabled while the
 * accordion row is expanded, so collapsed providers cost nothing.
 */
export function useOrgProviderConfig(providerId: string, enabled: boolean) {
  const { isAuthenticated, user } = useAuth();
  const isOrgAdmin = user?.role === 'admin';
  return useQuery<ProviderConfigDetail>({
    queryKey: detailKey(providerId),
    queryFn: () => identityApi.org.providerConfig(providerId).get(),
    enabled: isAuthenticated && isOrgAdmin && enabled,
    staleTime: 60 * 1000,
  });
}

function useInvalidateOrgProviders() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY_ROOT });
}

/**
 * Save one provider's config. The PUT returns the same masked shape as GET, so
 * we seed the detail cache directly and invalidate the subtree (refreshes the
 * index `configured` badge). Surfaces the server's 400 message to the caller.
 */
export function useSaveProviderConfig() {
  const qc = useQueryClient();
  const invalidate = useInvalidateOrgProviders();
  return useMutation<
    ProviderConfigDetail,
    Error,
    { providerId: string; values: Record<string, string> }
  >({
    mutationFn: ({ providerId, values }) =>
      identityApi.org.providerConfig(providerId).save({ values }),
    onSuccess: (data) => {
      qc.setQueryData(detailKey(data.providerId), data);
      invalidate();
    },
  });
}

export function useRemoveProviderConfig() {
  const invalidate = useInvalidateOrgProviders();
  return useMutation<{ providerId: string; deleted: boolean }, Error, string>({
    mutationFn: (providerId) =>
      identityApi.org.providerConfig(providerId).remove(),
    onSuccess: () => invalidate(),
  });
}
